import { useTranslation } from 'react-i18next';
import { HeaderSectionContainer } from './HeaderSection.styles';
import { Button, FormLabel, Stack, TextField, Typography } from '@mui/material';

interface HeaderSectionProps {
  isSubmitting?: boolean;
  /** Disables the submit button beyond the empty-title check (e.g. frozen audit). */
  submitDisabled?: boolean;
  fieldError?: boolean;
  fieldLabel: string;
  fieldPlaceholder: string;
  fieldValue: string;
  title: string;
  submitButtonText?: string;
  onDiscard: () => void;
  onSubmit: () => Promise<void>;
  onChangeName: (_e: React.ChangeEvent<HTMLInputElement>) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  isSubmitting,
  submitDisabled = false,
  fieldLabel,
  fieldPlaceholder,
  fieldError,
  fieldValue,
  submitButtonText,
  title,
  onSubmit,
  onChangeName,
  onDiscard,
}) => {
  const { t: tGlobal } = useTranslation('dashboard', { keyPrefix: 'global' });

  const submitButtonLabel = submitButtonText || tGlobal('create');

  return (
    <HeaderSectionContainer>
      <Stack direction='row' spacing={2} justifyContent='space-between' alignItems='center'>
        <Typography fontWeight={800} variant='h5'>
          {title}
        </Typography>
        <Stack direction='row' spacing={2}>
          <Button onClick={onDiscard} variant='text' color='error' data-testid='discard-button'>
            {tGlobal('discard')}
          </Button>
          <Button
            loading={isSubmitting}
            color='primary'
            disabled={!fieldValue || submitDisabled}
            variant='contained'
            // `onSubmit` is async; `onClick` wants a void return. The caller owns error
            // handling, so the promise is explicitly discarded rather than floated.
            onClick={() => void onSubmit()}
            data-testid='submit-button'
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <FormLabel>{fieldLabel}</FormLabel>
        <TextField
          fullWidth
          autoFocus
          slotProps={{
            inputLabel: {
              autoFocus: true,
            },
          }}
          error={fieldError}
          onChange={onChangeName}
          value={fieldValue}
          placeholder={fieldPlaceholder}
          data-testid='title-input'
        />
      </Stack>
    </HeaderSectionContainer>
  );
};

export default HeaderSection;
